/** Social inbox squares in Mi jornada: when it is time to check again. */

export type SocialChannelKey = "facebook" | "instagram" | "tiktok";

export interface SocialChannel {
  id: SocialChannelKey;
  key: SocialChannelKey;
  label: string;
  inbox_url: string | null;
  active: boolean;
  sort: number;
}

export interface SocialCheck {
  id: number;
  created_at: string;
  channel: SocialChannelKey;
  sales_id: number | null;
  alerts_closed: number;
}

export interface SocialAlert {
  id: number;
  created_at: string;
  channel: SocialChannelKey;
  kind: string;
  author: string | null;
  body: string | null;
  url: string | null;
  source: string | null;
  handled_at: string | null;
}

export interface SocialSettings {
  id: number;
  owner_ids: number[];
  check_every_hours: number;
}

export type SocialStatus =
  /** No account yet */
  | "inactive"
  /** Comments waiting (detected automatically) */
  | "alerts"
  /** Too long since the last check (or none today) */
  | "due"
  /** Checked recently */
  | "ok"
  /** Outside 9:00–21:00 */
  | "closed";

export const WORK_START_HOUR = 9;
export const WORK_END_HOUR = 21;

const startOfWorkday = (now: Date) => {
  const start = new Date(now);
  start.setHours(WORK_START_HOUR, 0, 0, 0);
  return start;
};

export const socialStatus = ({
  active,
  openAlerts,
  lastCheckAt,
  everyHours,
  now = new Date(),
}: {
  active: boolean;
  openAlerts: number;
  lastCheckAt?: string | null;
  everyHours: number;
  now?: Date;
}): SocialStatus => {
  if (!active) return "inactive";
  if (openAlerts > 0) return "alerts";
  const hour = now.getHours();
  if (hour < WORK_START_HOUR || hour >= WORK_END_HOUR) return "closed";
  if (!lastCheckAt) return "due";
  const last = new Date(lastCheckAt);
  if (last < startOfWorkday(now)) return "due";
  return now.getTime() - last.getTime() >= everyHours * 3_600_000
    ? "due"
    : "ok";
};

/** When the next check is due ("14:30"), or null if it already is. */
export const nextCheckAt = (
  lastCheckAt: string | null | undefined,
  everyHours: number,
  now = new Date(),
): Date | null => {
  if (!lastCheckAt) return null;
  const next = new Date(
    new Date(lastCheckAt).getTime() + everyHours * 3_600_000,
  );
  if (next <= now) return null;
  const end = new Date(now);
  end.setHours(WORK_END_HOUR, 0, 0, 0);
  if (next >= end) {
    // Tomorrow at the start of the day
    const tomorrow = startOfWorkday(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }
  return next;
};

export const timeAgo = (value: string, now = new Date()) => {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(value).getTime()) / 60_000),
  );
  if (minutes < 1) return "ahora mismo";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "ayer" : `hace ${days} días`;
};
